import { useEffect, useCallback, useRef, useState } from "react";
import { multiSocketService } from "@/shared/services/socketService";

type SocketCustomId = string | number | null;
type SocketEvent = string;
type SocketPayload = unknown;
type SocketCallback = (_data?: SocketPayload) => void;

interface HookSocket {
  connected: boolean;
  on: (_event: SocketEvent, _callback: SocketCallback) => void;
  off: (_event: SocketEvent, _callback: SocketCallback) => void;
}

export const useSocket = (
  url: string | null = "",
  customId: SocketCustomId = null
) => {
  const socketRef = useRef<HookSocket | null>(null);
  const eventHandlersRef = useRef<Map<SocketEvent, SocketCallback>>(new Map());
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!url) return;

    socketRef.current = multiSocketService.connect(url, customId);
    const eventHandlers = eventHandlersRef.current;

    const handleConnect = () => setIsConnected(true);
    const handleDisconnect = () => setIsConnected(false);

    socketRef.current.on("connect", handleConnect);
    socketRef.current.on("disconnect", handleDisconnect);

    setIsConnected(socketRef.current.connected);

    return () => {
      if (socketRef.current) {
        socketRef.current.off("connect", handleConnect);
        socketRef.current.off("disconnect", handleDisconnect);
        eventHandlers.forEach((callback, event) => {
          socketRef.current.off(event, callback);
        });
        eventHandlers.clear();
      }
      // Correct: Pass url and customId to disconnect the right socket
      multiSocketService.disconnect(url || "", customId);
    };
  }, [url, customId]); // <-- include customId

  const emit = useCallback(
    (event: SocketEvent, data?: SocketPayload) => {
      multiSocketService.emit(url, customId, event, data);
    },
    [url, customId]
  );

  const on = useCallback(
    (event: SocketEvent, callback: SocketCallback) => {
      eventHandlersRef.current.set(event, callback);
      multiSocketService.on(url || "", customId, event, callback);
    },
    [url, customId]
  );

  const off = useCallback(
    (event: SocketEvent) => {
      const callback = eventHandlersRef.current.get(event);
      if (callback) {
        multiSocketService.off(url || "", customId, event, callback);
        eventHandlersRef.current.delete(event);
      }
    },
    [url, customId]
  );

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      // socketRef.current.off("connect", handleConnect);
      // socketRef.current.off("disconnect", handleDisconnect);
      eventHandlersRef.current.forEach((callback, event) => {
        socketRef.current.off(event, callback);
      });
      eventHandlersRef.current.clear();
    }
    // Correct: Pass url and customId to disconnect the right socket
    multiSocketService.disconnect(url || "", customId);
  }, [url, customId]);

  // Provide a getter for the live socket reference to avoid consumers using a possibly stale null value.
  const getSocket = useCallback(() => socketRef.current, []);

  return {
    // Provide only getSocket() to access the live socket instance.
    // Avoid exposing a destructurable `socket` value which can be stale.
    getSocket, // call to retrieve current socket instance (may be null)
    emit,
    on,
    off,
    isConnected,
    disconnect,
  };
};
